import { AmbientGlow, GlassCard, ScrollReveal } from '@/components/design-system';
import CreateListingForm from '@/components/household/CreateListingForm';
import MyListingsList from '@/components/household/MyListingsList';
import SmartScanPanel from '@/components/household/SmartScanPanel';
import PageShell from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeUsers, type UsersResponse } from '@/lib/collections/users';
import type { SmartScanResult } from '@/utils/plastic-rates';
import { useAuth } from '@pooflabs/web';
import { Recycle, Wallet } from 'lucide-react';
import { useState } from 'react';

interface PendingScan {
  result: SmartScanResult;
  photoUrl?: string;
  previewUrl: string;
}

/**
 * Household dashboard: Smart Scan a photo of recovered plastic, publish it as
 * a listing, and track your own listings — the household side of the
 * recovery network.
 */
export function HouseholdPage() {
  const { user, login } = useAuth();
  const [pendingScan, setPendingScan] = useState<PendingScan | null>(null);
  const [resetToken, setResetToken] = useState(0);

  const { data: profile, loading: profileLoading } = useRealtimeData<UsersResponse | null>(
    subscribeUsers,
    !!user?.address,
    user?.address ?? ''
  );

  if (!user?.address) {
    return (
      <PageShell>
        <div className="container flex min-h-[70vh] items-center justify-center">
          <ScrollReveal className="relative w-full max-w-md">
            <AmbientGlow color="amber" size={360} position="top-0 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            <GlassCard padding="lg" className="text-center">
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 ring-1 ring-border/60">
                <Wallet className="h-6 w-6 text-primary" strokeWidth={2} />
              </div>
              <span className="text-overline">Household Dashboard</span>
              <h1 className="font-heading mt-2 text-2xl font-bold text-foreground">Connect to get started</h1>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Connect your wallet to Smart Scan your plastic and publish recovery listings.
              </p>
              <Button
                onClick={() => login()}
                size="lg"
                className="mt-6 w-full bg-gradient-to-r from-primary to-accent text-primary-foreground hover:opacity-90"
              >
                Connect wallet
              </Button>
            </GlassCard>
          </ScrollReveal>
        </div>
      </PageShell>
    );
  }

  if (!profileLoading && profile && profile.role !== 'household') {
    return (
      <PageShell>
        <div className="container flex min-h-[70vh] items-center justify-center">
          <ScrollReveal className="relative w-full max-w-md">
            <AmbientGlow color="primary" size={360} position="top-0 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            <GlassCard padding="lg" className="text-center">
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 ring-1 ring-border/60">
                <Recycle className="h-6 w-6 text-primary" strokeWidth={2} />
              </div>
              <span className="text-overline">Household Dashboard</span>
              <h1 className="font-heading mt-2 text-2xl font-bold text-foreground">Household-only space</h1>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                This dashboard is for households recovering plastic. Your profile is set up as{' '}
                <span className="font-medium text-foreground">{profile.role}</span>.
              </p>
            </GlassCard>
          </ScrollReveal>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="container relative py-8">
        <AmbientGlow color="amber" size={420} position="top-0 right-0 translate-x-1/3 -translate-y-1/3" />
        <AmbientGlow color="primary" size={380} position="bottom-0 left-0 -translate-x-1/3 translate-y-1/3" />

        <ScrollReveal className="mb-8">
          <span className="text-overline">Household Dashboard</span>
          <h1 className="font-heading mt-1 text-3xl font-bold text-foreground sm:text-4xl">
            Turn what you&apos;ve recovered into worth
          </h1>
        </ScrollReveal>

        <div className="relative grid gap-6 lg:grid-cols-2 lg:items-start">
          <ScrollReveal delay={0.05}>
            <SmartScanPanel
              resetToken={resetToken}
              onScanComplete={(result, photoUrl, previewUrl) => setPendingScan({ result, photoUrl, previewUrl })}
            />
          </ScrollReveal>

          <ScrollReveal delay={0.12}>
            {pendingScan ? (
              <CreateListingForm
                scan={pendingScan.result}
                photoUrl={pendingScan.photoUrl}
                previewUrl={pendingScan.previewUrl}
                userAddress={user.address}
                defaultCity={profile?.city}
                onCreated={() => {
                  setPendingScan(null);
                  setResetToken((t) => t + 1);
                }}
              />
            ) : (
              <GlassCard padding="lg" className="flex h-full min-h-[20rem] items-center justify-center text-center">
                <div>
                  <Recycle className="mx-auto mb-3 h-8 w-8 text-muted-foreground" strokeWidth={1.5} />
                  <p className="text-sm text-muted-foreground">
                    Run a Smart Scan to pre-fill a listing here.
                  </p>
                </div>
              </GlassCard>
            )}
          </ScrollReveal>
        </div>

        <div className="relative mt-10">
          <MyListingsList userAddress={user.address} />
        </div>
      </div>
    </PageShell>
  );
}

export default HouseholdPage;
